const SEPARATOR_PAIRS = "&";
const SEPARATOR_KEY_VALUE = "=";

function prependNotEmpty(prefix, string) {
	return string ? `${prefix}${string}` : string;
}

const join = keyValues =>
	keyValues
		.map(
			([key, value]) =>
				`${key}${value ? `${SEPARATOR_KEY_VALUE}${value}` : ""}`,
		)
		.join(SEPARATOR_PAIRS);

/**
 * Example for simple string values:
 * ```
 * 	parse({
 * 		"param": null
 * 	})("#param=123")
 * 	===
 * 	{"param":"123"}
 * ```
 *
 * Example for mapped value:
 * ```
 * 	parse({
 * 		"param": parseInt
 * 	})("#param=123")
 * 	===
 * 	{"param":123}
 * ```
 *
 * Example for renamed parameter:
 * ```
 * 	parse({
 * 		"param": ["renamed"]
 * 	})("#param=123")
 * 	===
 * 	{"renamed":"123"}
 * ```
 *
 * Example for renamed parameter with mapped value:
 * ```
 * 	parse({
 * 		"param": ["renamed", parseInt]
 * 	})("#param=123")
 * 	===
 * 	{"renamed":123}
 * ```
 */
export const parse = parsers => hash =>
	Object.fromEntries(
		hash
			.replace("#", "")
			.split(SEPARATOR_PAIRS)
			.map(pairs => pairs.split(SEPARATOR_KEY_VALUE))
			.filter(([key]) => parsers.hasOwnProperty(key))
			.map(([key, val]) =>
				parsers[key]
					? typeof parsers[key] === "function"
						? [key, parsers[key](val)]
						: [
								parsers[key][0],
								typeof parsers[key][1] === "function"
									? parsers[key][1](val)
									: val,
							]
					: [key, val],
			),
	);

/**
 * Example for simple value:
 * ```
 * 	format({param:null})({param:123,ignore:this})
 * 	===
 * 	"#param=123"
 * ```
 *
 * Example for mapped value:
 * ```
 * 	format({param:value => value == 123 ? "Yes" : "No"})({param:123,ignore:this})
 * 	===
 * 	"#param=Yes"
 * ```
 *
 * Example for renamed parameter:
 * ```
 * 	format({param:["renamed"]})({param:123,ignore:this})
 * 	===
 * 	"#renamed=123"
 * ```
 *
 * Example for renamed parameter with mapped value:
 * ```
 * 	format({param:["renamed", value => value == 123 ? "Yes" : "No"]})({param:123,ignore:this})
 * 	===
 * 	"#renamed=Yes"
 * ```
 */
export const format = formatters => data =>
	prependNotEmpty(
		"#",
		join(
			Object.entries(formatters)
				.map(([key, formatter]) =>
					formatter
						? typeof formatter === "function"
							? [key, formatter(data[key])]
							: [
									formatter[0],
									typeof formatter[1] === "function"
										? formatter[1](data[key])
										: data[key],
								]
						: [key, data[key]],
				)
				.filter(([key, value]) => key && value),
		),
	);

const buildUrl = hash => location.pathname + location.search + hash;

/**
 * @param {string} hash
 * @param {Array<string>} params
 */
export function stripHashParams(hash, params) {
	const remainingHashValues = hash
		.replace("#", "")
		.split(SEPARATOR_PAIRS)
		.map(pairs => pairs.split(SEPARATOR_KEY_VALUE))
		.filter(([key]) => !params.includes(key));
	return prependNotEmpty("#", join(remainingHashValues));
}

export function pushState(hash) {
	history.pushState(null, "", buildUrl(hash));
}

export function replaceState(hash) {
	history.replaceState(null, "", buildUrl(hash));
}

/**
 * Only needed during transitioning from legacy search-param based travelData to new hash based ones.
 *
 * @deprecated
 * @param {Array<string>} params
 * @returns {URL}
 */
export function convertLegacySearchParams(params) {
	const url = new URL(window.location);
	let hash = url.hash.replace("#", "");
	const hashParams = Object.fromEntries(
		hash.split(SEPARATOR_PAIRS).map(pairs => pairs.split(SEPARATOR_KEY_VALUE)),
	);
	params
		.filter(param => url.searchParams.has(param))
		.forEach(param => {
			const value = url.searchParams.get(param);

			if (!hashParams.hasOwnProperty(param)) {
				hash = `${hash ? `${hash}${SEPARATOR_PAIRS}` : ""}${join([[param, value]])}`;
			}
			return url.searchParams.delete(param);
		});
	url.hash = hash;
	window.history.replaceState(null, "", url.toString());
	return url;
}
